import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import ReactApexChart from "react-apexcharts";

import { adminUserActivity } from "../../../api/api.service";
import {
  CONTEXT_LABELS,
  GRADE_BAND_LABELS,
  RETURN_TO_TASK_LABELS,
  RETURN_TO_TASK_ORDER,
  STATE_COLORS,
  STATE_LABELS,
  formatDate,
  formatMinutes,
} from "./insightLabels";

const RANGES = [
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "12 months" },
];

const GREEN = "#438d40";
const BUCKET_COLORS = ["#438d40", "#8cbf5a", "#e6b94a", "#d9654b"];

const isoDay = (date) => date.toISOString().slice(0, 10);

const rangeParams = (days) => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  return { startDate: isoDay(start), endDate: isoDay(end) };
};

/** Daily counts rolled into weeks once the range is long enough to be noise day by day. */
const toSeries = (dailyVolume = [], days) => {
  if (days <= 31) return dailyVolume.map((d) => ({ x: d.date, y: d.count }));
  const weeks = [];
  dailyVolume.forEach((d, i) => {
    if (i % 7 === 0) weeks.push({ x: d.date, y: 0 });
    weeks[weeks.length - 1].y += d.count;
  });
  return weeks;
};

function Tile({ label, value, hint }) {
  return (
    <div className="cd-tile">
      <span className="cd-tile__label">{label}</span>
      <span className="cd-tile__value">{value}</span>
      {hint ? <span className="cd-tile__hint">{hint}</span> : null}
    </div>
  );
}

function Panel({ title, caption, children, className = "" }) {
  return (
    <section className={`cd-panel ${className}`}>
      <header className="cd-panel__head">
        <h3>{title}</h3>
        {caption ? <p>{caption}</p> : null}
      </header>
      {children}
    </section>
  );
}

const Empty = ({ children }) => <p className="cd-empty">{children}</p>;

/**
 * What this teacher actually does with Mind Circuit — the same figures their
 * own Class Insights shows, across every classroom they have used.
 */
export default function Overview({ overview }) {
  const { id } = useParams();
  const [days, setDays] = useState(30);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    adminUserActivity(id, rangeParams(days))
      .then((res) => {
        if (!cancelled) setActivity(res?.data ?? res);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, days]);

  const summary = activity?.summary;
  const series = useMemo(
    () => toSeries(activity?.dailyVolume, days),
    [activity, days],
  );
  const returnCounts = RETURN_TO_TASK_ORDER.map(
    (bucket) =>
      activity?.returnToTask?.find((row) => row.bucket === bucket)?.count ?? 0,
  );
  const returnTotal = returnCounts.reduce((a, b) => a + b, 0);
  const topActivities = (activity?.perActivity ?? [])
    .slice()
    .sort((a, b) => b.uses - a.uses)
    .slice(0, 6);
  // The moment ("after lunch", "before a test") is no longer asked when a
  // reset starts, so for current data it is almost always unrecorded. Show it
  // only when some was recorded; otherwise show which state the class was in,
  // which every reset carries.
  const contexts = (activity?.contextMix ?? [])
    .filter((c) => c.count > 0 && c.context !== "UNSPECIFIED")
    .sort((a, b) => b.count - a.count);
  const states = (activity?.stateMix ?? [])
    .filter((s) => s.count > 0)
    .sort((a, b) => b.count - a.count);
  const contextMax = Math.max(1, ...contexts.map((c) => c.count));
  const stateMax = Math.max(1, ...states.map((s) => s.count));
  const videos = overview?.videos ?? { started: 0, completed: 0 };

  return (
    <div className="cd-overview">
      <div className="cd-overview__bar">
        <p className="cd-overview__scope">
          Resets this teacher started, across every classroom they use
          {activity?.timezone ? ` · days in ${activity.timezone}` : ""}
        </p>
        <div className="cd-range" role="group" aria-label="Date range">
          {RANGES.map((range) => (
            <button
              key={range.days}
              type="button"
              aria-pressed={days === range.days}
              className={days === range.days ? "is-active" : ""}
              onClick={() => setDays(range.days)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {failed ? (
        <Empty>
          Could not load this teacher's activity. Try again in a moment.
        </Empty>
      ) : null}

      <div className={`cd-tiles ${loading ? "is-loading" : ""}`}>
        <Tile
          label="Resets run"
          value={summary?.totalResetEvents ?? "—"}
          hint={
            summary
              ? `on ${summary.daysWithDocumentedUse} of ${summary.rangeDays} days`
              : null
          }
        />
        <Tile
          label="Class time saved"
          value={formatMinutes(summary?.timeRecoveredMinutes)}
          hint={
            summary?.timeWithoutResetsMinutes
              ? `vs ${formatMinutes(summary.timeWithoutResetsMinutes)} without resets`
              : "No return times logged yet"
          }
        />
        <Tile
          label="Feedback given"
          value={
            summary?.outcomeLoggingCompleteness != null
              ? `${Math.round(summary.outcomeLoggingCompleteness)}%`
              : "—"
          }
          hint={
            summary?.totalResetEvents
              ? "of resets have the feedback form answered"
              : "No resets in this range"
          }
        />
        <Tile
          label="Different resets tried"
          value={summary?.uniqueResetsUsed ?? "—"}
          hint={
            topActivities[0]
              ? `Most used: ${topActivities[0].title}`
              : "No resets in this range"
          }
        />
        <Tile
          label="Videos finished"
          value={videos.completed}
          hint={
            videos.started ? `of ${videos.started} started` : "None started"
          }
        />
      </div>

      <div className="cd-grid">
        <Panel
          title="Resets over time"
          caption={days > 31 ? "Per week" : "Per day"}
          className="cd-grid__wide"
        >
          {series.some((point) => point.y > 0) ? (
            <ReactApexChart
              type="bar"
              height={230}
              series={[{ name: "Resets", data: series }]}
              options={{
                chart: {
                  toolbar: { show: false },
                  fontFamily: "inherit",
                  animations: { speed: 350 },
                },
                colors: [GREEN],
                plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
                dataLabels: { enabled: false },
                grid: { borderColor: "#eef1ee", strokeDashArray: 3 },
                xaxis: {
                  type: "datetime",
                  labels: { datetimeUTC: false, style: { colors: "#6b7a6b" } },
                  axisBorder: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: {
                  labels: {
                    formatter: (v) => Math.round(v),
                    style: { colors: "#6b7a6b" },
                  },
                },
                tooltip: {
                  x: { format: days > 31 ? "'Week of' MMM d" : "ddd, MMM d" },
                },
              }}
            />
          ) : (
            <Empty>No resets in this range.</Empty>
          )}
        </Panel>

        <Panel
          title="How fast the class came back"
          caption={returnTotal ? `${returnTotal} answers` : null}
        >
          {returnTotal ? (
            <ReactApexChart
              type="donut"
              height={230}
              series={returnCounts}
              options={{
                labels: RETURN_TO_TASK_ORDER.map(
                  (b, i) => `${RETURN_TO_TASK_LABELS[b]} (${returnCounts[i]})`,
                ),
                colors: BUCKET_COLORS,
                legend: { position: "bottom", fontFamily: "inherit" },
                dataLabels: { enabled: false },
                stroke: { width: 2 },
                plotOptions: { pie: { donut: { size: "68%" } } },
                chart: { fontFamily: "inherit" },
              }}
            />
          ) : (
            <Empty>No return-to-task answers yet.</Empty>
          )}
        </Panel>

        <Panel title="Most-used resets">
          {topActivities.length ? (
            <ul className="cd-rank">
              {topActivities.map((item) => (
                <li key={item.key}>
                  <span
                    className="cd-dot"
                    style={{
                      background: STATE_COLORS[item.stateKey] || "#9aa59a",
                    }}
                  />
                  <span className="cd-rank__name">
                    {item.title}
                    <small>{STATE_LABELS[item.stateKey] || "—"}</small>
                  </span>
                  <span className="cd-rank__count">{item.uses}</span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>No resets in this range.</Empty>
          )}
        </Panel>

        {contexts.length ? (
          <Panel title="When they reach for a reset">
            <ul className="cd-bars">
              {contexts.map((c) => (
                <li key={c.context}>
                  <span className="cd-bars__label">
                    {CONTEXT_LABELS[c.context] || c.context}
                  </span>
                  <span className="cd-bars__track">
                    <span
                      className="cd-bars__fill"
                      style={{ width: `${(c.count / contextMax) * 100}%` }}
                    />
                  </span>
                  <span className="cd-bars__count">{c.count}</span>
                </li>
              ))}
            </ul>
          </Panel>
        ) : (
          <Panel title="What the class needed" caption="By state">
            {states.length ? (
              <ul className="cd-bars">
                {states.map((s) => (
                  <li key={s.stateKey}>
                    <span className="cd-bars__label">
                      <span
                        className="cd-dot me-2"
                        style={{ background: STATE_COLORS[s.stateKey] }}
                      />
                      {STATE_LABELS[s.stateKey] || s.stateKey}
                    </span>
                    <span className="cd-bars__track">
                      <span
                        className="cd-bars__fill"
                        style={{
                          width: `${(s.count / stateMax) * 100}%`,
                          background: STATE_COLORS[s.stateKey],
                        }}
                      />
                    </span>
                    <span className="cd-bars__count">{s.count}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>No resets in this range.</Empty>
            )}
          </Panel>
        )}

        <Panel
          title="Classrooms"
          caption={`All time${overview?.favourites ? ` · ${overview.favourites} ${overview.favourites === 1 ? "reset" : "resets"} saved as favourites` : ""}`}
          className="cd-grid__wide"
        >
          {overview?.classrooms?.length ? (
            <ul className="cd-rooms">
              {overview.classrooms.map((room) => (
                <li key={room.id} className={room.isActive ? "is-active" : ""}>
                  <span className="cd-rooms__name">
                    {room.name}
                    {room.isActive ? <em>Current</em> : null}
                  </span>
                  <span className="cd-rooms__meta">
                    {room.gradeBand
                      ? GRADE_BAND_LABELS[room.gradeBand]
                      : "Grade not set"}
                  </span>
                  <span className="cd-rooms__stat">
                    <strong>{room.resets}</strong> resets
                  </span>
                  <span className="cd-rooms__meta">
                    Last used {formatDate(room.lastResetAt)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>No classroom set up yet.</Empty>
          )}
        </Panel>
      </div>
    </div>
  );
}
